import React, { useState } from 'react';
import { BudgetCategoryRowViewModel } from 'src/types/view-models/budgets/budget-category-row.view.model';
import LiteralsService from 'src/services/literals/LiteralsService';
import { ButtonSecondary, IconClose, SelectOption } from '@americanexpress/dls-react';
import { useBudget } from 'src/hooks/queries/budgets';
import { useCategories } from 'src/hooks/queries/categories';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';
import styles from './BudgetCategoryRow.module.scss';
import layout from 'src/styles/layout.module.scss';
import MonthlyAverageBox from 'src/components/atomic/molecules/monthlyAverageBox/MonthlyAverageBox';
import IconButton from 'src/components/icon-button/IconButton';
import { DLSSize } from 'src/types/dls-size.enum';
import { CategoryModel } from 'src/types/category.model';
import find from 'lodash/find';
import { useFormikContext } from 'formik';
import { BudgetCreateFormValuesModel } from 'src/types/form-models/budget-create-form-values.model';
import FormatService from 'src/services/format.service';
import SelectDetailed from 'src/components/formik-fields/selectDetailed/SelectDetailed';
import CategoryService from 'src/services/category.service';
import { ButtonType } from 'src/types/button-type.enum';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useAccountSyncStatuses } from 'src/hooks/queries/accounts';
import AccountService from 'src/services/account.service';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import CategoryHistoryModal from 'src/components/atomic/organisms/transaction/category-history-modal/CategoryHistoryModal';
import { useSpendingHistory } from 'src/hooks/queries/spending';
import { LocalDate } from '@js-joda/core';
import { TransactionType } from 'src/types/transaction-type.enum';
import { getCategoryTagName } from 'src/utils/category.utils';

export default function BudgetCategoryRow({
    index,
    removeRow,
    removeSubcategories,
    hasAvailableSubcategories,
    addSubcategory
}: BudgetCategoryRowViewModel): JSX.Element {
    const { values } = useFormikContext<BudgetCreateFormValuesModel>();
    const { isLoading: isLoadingCategories, data: categories } = useCategories(TransactionType.TRANSACTION_EXPENSE);
    const selectedGroupId = values.budgets[index].groupId || null;
    const { data: budget } = useBudget(selectedGroupId);
    const { isLoading: isLoadingSpendingHistory, data: spendingHistory } = useSpendingHistory({
        id: selectedGroupId,
        startDate: LocalDate.now().minusYears(1).withDayOfMonth(1),
        endDate: LocalDate.now().withDayOfMonth(1),
        enabled: !!selectedGroupId
    });
    const { data: syncStatuses } = useAccountSyncStatuses();
    const [openCategoryHistoryModal, setOpenCategoryHistoryModal] = useState<boolean>();
    const { isMinWidthLarge } = useBreakpoints();
    const location = useLocation();
    // Parent component handles loading state
    if (isLoadingCategories) return <></>;

    const isSyncingAccounts: boolean = AccountService.isSyncingAccounts(syncStatuses);
    const spendingHistoryForBudget = spendingHistory?.[0];
    const monthlyAverageAmount = selectedGroupId
        ? FormatService.format(spendingHistoryForBudget?.averageSpent || 0)
        : '$--';
    const percentMonthlySpend = selectedGroupId
        ? Math.round((spendingHistoryForBudget?.averageSpentPercent || 0) * 100)
        : 'NN';

    const parentId: string =
        values.budgets[index].parentGroupId ||
        find(categories, (category) => category.id === budget?.groupId)?.parentId;
    const parentName: string = find(categories, (category) => category.id === parentId)?.name;
    const isSubcategory: boolean = parentId?.length > 0;

    /** All category options */
    const categoryOptions: CategoryModel[] = categories.filter((category) => {
        return isSubcategory ? category.parentId === parentId : !category.parentId;
    });

    /** Filtered out other selected categories */
    const filteredCategoryOptions = categoryOptions.filter(
        ({ id }) => !values.budgets.map(({ groupId }) => groupId).includes(id) || id === selectedGroupId
    );

    const categorySelectOptions: JSX.Element[] = categoryOptions.map((category) => {
        return (
            <SelectOption key={`${index}-${category.id}`} data-label={parentName} value={category.id}>
                {category.name}
            </SelectOption>
        );
    });

    const handleTrackClick = (category: CategoryModel): void => {
        const categoryTag: string = getCategoryTagName(category, categories);
        const fullTagName: string =
            adobeTags.Personalize[isSubcategory ? 'Subcategory' : 'Category'].Dropdown + '>' + categoryTag;
        trackClick(location.pathname, fullTagName);
    };

    const categoryDropdown = categorySelectOptions[0].props ? (
        <SelectDetailed
            id={`budgets[${index}].groupId`}
            className={isSubcategory ? styles.subcategoryDropdown : styles.parentCategoryDropdown}
            label={LiteralsService.getLiteral(
                isSubcategory ? 'Budget.Create.subcategoryLabel' : 'Budget.Create.categoryLabel'
            )}
            options={isSubcategory ? filteredCategoryOptions : CategoryService.addIcons(filteredCategoryOptions)}
            customTrackClick={handleTrackClick}
            onChange={removeSubcategories}
            weak={isSubcategory}
        />
    ) : null;

    const amountInput = (
        <CurrencyInput
            id={`budgets[${index}].monthlyAmount`}
            label={LiteralsService.getLiteral('Budget.Create.amountLabel')}
            className={styles.amountInput}
            tagName={adobeTags.Personalize[isSubcategory ? 'Subcategory' : 'Category'].AmountFill}
        />
    );

    const addCategoryButtonAccessibleLabel: string = LiteralsService.getLiteral(
        'Budget.Create.addSpecificSubcategory',
        [budget?.groupName || LiteralsService.getLiteral('Budget.Create.new')]
    );
    const removeCategoryButtonAccessibleLabel: string = LiteralsService.getLiteral(
        'Budget.Create.removeSpecificCategory',
        [
            budget?.groupName || LiteralsService.getLiteral('Budget.Create.new'),
            isSubcategory
                ? LiteralsService.getLiteral('Budget.Create.subcategoryLabel')
                : LiteralsService.getLiteral('Budget.Create.categoryLabel')
        ]
    );

    const removeCategoryIconButton = (
        <IconButton
            variant={ButtonType.SECONDARY}
            onClick={() => {
                removeRow();
                trackClick(location.pathname, adobeTags.Personalize[isSubcategory ? 'Subcategory' : 'Category'].Delete);
            }}
            icon={<IconClose />}
            title={removeCategoryButtonAccessibleLabel}
            className={styles.hideOnMobile}
            size={DLSSize.MEDIUM}
            data-cy={Selectors.SpendSmart.DELETE_BUTTON}
        />
    );

    const removeCategoryTextButton = (
        <ButtonSecondary
            size={DLSSize.SMALL}
            onClick={() => {
                removeRow();
                trackClick(location.pathname, adobeTags.Personalize[isSubcategory ? 'Subcategory' : 'Category'].Delete);
            }}
            className={`${styles.mobileFullWidthButton}`}
            aria-label={removeCategoryButtonAccessibleLabel}
        >
            {isSubcategory
                ? LiteralsService.getLiteral('Budget.Create.removeSubcategory')
                : LiteralsService.getLiteral('Budget.Create.removeCategory')}
        </ButtonSecondary>
    );

    return (
        <>
            {openCategoryHistoryModal && (
                <CategoryHistoryModal groupId={selectedGroupId} closeModal={() => setOpenCategoryHistoryModal(false)} />
            )}
            <p id={`categoryRowScreenReaderLabel${index}`} className={layout.screenReaderOnly}>
                {budget?.groupName || LiteralsService.getLiteral('Budget.Create.newBudget')}
            </p>
            <div
                className={`${isSubcategory ? styles.subcategoryRowWrapper : styles.parentCategoryRowWrapper} ${
                    index === 0 ? styles.noBorderTop : ''
                }`}
                role="group"
                aria-labelledby={`categoryRowScreenReaderLabel${index}`}
            >
                <div className={styles.subcategoryFlex}>
                    {isSubcategory && (
                        <div
                            className={styles.subcategoryLeftIndicator}
                            data-cy={Selectors.SpendSmart.SUBCATEGORY_LEFT_INDICATOR}
                        />
                    )}
                    <div className={styles.budgetCategoryRow} data-cy={Selectors.SpendSmart.ROW}>
                        {isMinWidthLarge ? (
                            <>
                                <div>{categoryDropdown}</div>
                                <div>{amountInput}</div>
                            </>
                        ) : (
                            <div className={styles.stackedFields}>
                                {categoryDropdown}
                                {amountInput}
                            </div>
                        )}
                        <MonthlyAverageBox
                            amount={monthlyAverageAmount}
                            subtext={
                                <Typography
                                    variant={TypographyVariants.SUBTEXT}
                                    component="p"
                                    aria-live="polite"
                                    aria-busy={isSyncingAccounts}
                                >
                                    {LiteralsService.getLiteral('Budget.Create.monthlyAveragePercent', [
                                        isSyncingAccounts ? '--' : percentMonthlySpend
                                    ])}
                                </Typography>
                            }
                            id={`monthlyAverageBox-${index}`}
                            onClick={() => setOpenCategoryHistoryModal(true)}
                            tag={adobeTags.Personalize.Modal}
                            className={styles.monthlyAverage}
                            tagName={adobeTags.Tooltip[isSubcategory ? 'Subcategory' : 'Category']}
                            linkText={LiteralsService.getLiteral('Budget.Create.viewCategoryHistory')}
                            tooltipClassName={styles.monthlyAverageBoxTooltipButton}
                            loadingAmount={isLoadingSpendingHistory}
                            categoryName={budget?.groupName}
                        />
                        {removeCategoryIconButton}
                    </div>
                </div>
                {!isSubcategory && (
                    <ButtonSecondary
                        size={DLSSize.SMALL}
                        disabled={!hasAvailableSubcategories || !budget}
                        onClick={() => {
                            addSubcategory();
                            trackClick(location.pathname, adobeTags.Personalize.Subcategory.Add);
                        }}
                        className={styles.subcategoryButton}
                        aria-label={addCategoryButtonAccessibleLabel}
                        data-cy={Selectors.SpendSmart.SUBCATEGORY_BUTTON}
                    >
                        {LiteralsService.getLiteral('Budget.Create.addSubcategory')}
                    </ButtonSecondary>
                )}
                {removeCategoryTextButton}
            </div>
        </>
    );
}
